import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Label, Textarea, TextInput } from "flowbite-react";
import Modal from "./AnimatedModal";
import {
  HiOutlineCheckCircle,
  HiOutlineExclamation,
  HiOutlineInformationCircle,
  HiOutlineTrash,
  HiOutlineXCircle,
} from "react-icons/hi";
import { registerDialogHost } from "../../utils/dialogs";

/**
 * Desenha as modais chamadas por `utils/dialogs.js` (confirmDialog, alertDialog,
 * promptDialog). Montado uma única vez em `index.js`, fora das rotas, para
 * funcionar também nas telas públicas e por cima de outros modais.
 *
 * Mesmo padrão visual das modais do sistema (SaleComputeModal etc.): cabeçalho
 * pequeno com ícone, corpo `text-sm`, rodapé com "Cancelar" (light) e a ação
 * principal (failure). Só usa classes de cor que já têm versão no tema escuro.
 *
 * Vários pedidos ao mesmo tempo entram numa fila e aparecem um por vez.
 */

const TONES = {
  danger: { Icon: HiOutlineTrash, icon: "text-red-600" },
  warning: { Icon: HiOutlineExclamation, icon: "text-amber-600" },
  info: { Icon: HiOutlineInformationCircle, icon: "text-blue-600" },
  success: { Icon: HiOutlineCheckCircle, icon: "text-emerald-600" },
  error: { Icon: HiOutlineXCircle, icon: "text-red-600" },
};

const DEFAULT_TONE = { confirm: "warning", alert: "error", prompt: "info" };
const CLOSE_ANIMATION_MS = 200;
const Z_INDEX = 10100; // acima dos modais empilhados do card (até 10001)

const CONFIRM_ID = "relinq-dialog-confirm";
const CANCEL_ID = "relinq-dialog-cancel";

export default function DialogHost() {
  const [active, setActive] = useState(null);
  const [visible, setVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const queueRef = useRef([]);
  const activeRef = useRef(null);

  const showNext = useCallback(() => {
    const next = queueRef.current.shift() || null;
    activeRef.current = next;
    setActive(next);
    setVisible(Boolean(next));
    if (next?.kind === "prompt") {
      setInputValue(next.defaultValue ?? "");
      setInputError("");
    }
  }, []);

  useEffect(
    () =>
      registerDialogHost((descriptor) => {
        queueRef.current.push(descriptor);
        if (!activeRef.current) showNext();
      }),
    [showNext],
  );

  const finish = useCallback(
    (value) => {
      const current = activeRef.current;
      if (!current) return;
      activeRef.current = null;
      setVisible(false);
      current.resolve(value);
      // Mantém o conteúdo na tela durante a animação de saída.
      window.setTimeout(() => {
        if (!activeRef.current) showNext();
      }, CLOSE_ANIMATION_MS);
    },
    [showNext],
  );

  // Foco: perguntas de exclusão começam em "Cancelar" (Enter sem querer não
  // apaga nada); as demais, no botão principal.
  useEffect(() => {
    if (!visible || !active || active.kind === "prompt") return undefined;
    const tone = active.tone || DEFAULT_TONE[active.kind];
    const targetId =
      active.kind === "confirm" && tone === "danger" ? CANCEL_ID : CONFIRM_ID;
    const timer = window.setTimeout(() => {
      document.getElementById(targetId)?.focus();
    }, 60);
    return () => window.clearTimeout(timer);
  }, [visible, active]);

  if (!active) return null;

  const { kind } = active;
  const toneKey = active.tone && TONES[active.tone] ? active.tone : DEFAULT_TONE[kind];
  const { Icon, icon } = TONES[toneKey];
  const HeaderIcon = active.icon || Icon;
  const cancelValue = kind === "confirm" ? false : kind === "prompt" ? null : undefined;

  const confirmLabel =
    active.confirmLabel ||
    (kind === "alert" ? active.buttonLabel || "Entendi" : kind === "prompt" ? "Salvar" : "Confirmar");
  const cancelLabel = active.cancelLabel || "Cancelar";

  const submitPrompt = () => {
    const value = String(inputValue ?? "");
    const trimmed = value.trim();
    if (active.required && !trimmed) {
      setInputError(active.requiredMessage || "Preencha este campo.");
      return;
    }
    const validationError = active.validate ? active.validate(value) : "";
    if (validationError) {
      setInputError(validationError);
      return;
    }
    finish(value);
  };

  const handleConfirm = () => {
    if (kind === "prompt") return submitPrompt();
    return finish(kind === "confirm" ? true : undefined);
  };

  return (
    <Modal
      dismissible
      show={visible}
      size="md"
      onClose={() => finish(cancelValue)}
      className="!z-[10100]"
      style={{ zIndex: Z_INDEX }}
    >
      <Modal.Header className="py-2">
        <div className="flex items-center gap-2 text-sm md:text-base">
          <HeaderIcon className={`h-5 w-5 shrink-0 ${icon}`} />
          {active.title || (kind === "alert" ? "Aviso" : "Confirmação")}
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="space-y-3 text-sm text-gray-700">
          {active.message ? (
            <p className="whitespace-pre-line break-words">{active.message}</p>
          ) : null}

          {active.subject ? (
            <div className="break-words rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-semibold text-gray-900">
              {active.subject}
            </div>
          ) : null}

          {active.note ? (
            <div className="whitespace-pre-line break-words rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
              {active.note}
            </div>
          ) : null}

          {kind === "prompt" ? (
            <div>
              {active.label ? (
                <Label htmlFor="relinq-dialog-input" value={active.label} />
              ) : null}
              {active.multiline ? (
                <Textarea
                  id="relinq-dialog-input"
                  rows={active.rows || 5}
                  autoFocus
                  placeholder={active.placeholder}
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    setInputError("");
                  }}
                  color={inputError ? "failure" : undefined}
                />
              ) : (
                <TextInput
                  id="relinq-dialog-input"
                  type={active.inputType || "text"}
                  autoFocus
                  autoComplete="off"
                  placeholder={active.placeholder}
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    setInputError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submitPrompt();
                    }
                  }}
                  color={inputError ? "failure" : undefined}
                />
              )}
              {inputError ? (
                <p className="mt-1 text-xs text-red-600">{inputError}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      </Modal.Body>

      <Modal.Footer className="flex justify-end gap-2 py-3">
        {kind !== "alert" ? (
          <Button
            id={CANCEL_ID}
            color="light"
            onClick={() => finish(cancelValue)}
          >
            {cancelLabel}
          </Button>
        ) : null}
        <Button id={CONFIRM_ID} color="failure" onClick={handleConfirm}>
          {confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
